import { useState, useEffect } from 'react'
import { getProjectOptions } from '@/lib/api/projects'
import type { ProjectType, ConstructionType, ClientDesignation } from '@/types'

export interface ProjectOptions {
  projectTypes: Array<{ value: ProjectType; label: string; icon: string }>
  designations: Array<{ value: ClientDesignation; label: string }>
  constructionTypes: Array<{
    value: ConstructionType
    label: string
    description: string
    icon: string
  }>
  suppliers: Array<{ id: string; name: string; isDefault: boolean }>
  materials: {
    facade: string[]
    carcass: string[]
    worktop: string[]
  }
  colors: string[]
  finishes: string[]
}

/**
 * Hook to load dynamic project options from backend
 * 
 * These options are organization-specific and may vary per user.
 * Loaded once on wizard mount.
 */
export function useProjectOptions() {
  const [options, setOptions] = useState<ProjectOptions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    async function loadOptions() {
      try {
        setIsLoading(true)
        const data = await getProjectOptions()

        if (isMounted) {
          setOptions(data)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error)
          console.error('[useProjectOptions] Error loading options:', err)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadOptions()

    return () => {
      isMounted = false
    }
  }, [])

  return { options, isLoading, error }
}
